import React, {useMemo, useState} from "react";
import dayjs from "dayjs";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {ProgressSpinner} from "primereact/progressspinner";

import {fetchItemSalesRateAndRestock, fetchItemSlowAndNoSales} from "../service/AnalyticService";
import {ItemAnalyticsPrint} from "../print/ItemAnalyticsPrint";
import {ItemSlowNoSalesPrint} from "../print/ItemSlowNoSalesPrint";
import {MunshiMenu} from "../components/Menu";

export default function ItemAnalytics({handle}) {
    const [leadTimeDays, setLeadTimeDays] = useState(60);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const [mode, setMode] = useState("RESTOCK"); // RESTOCK | SLOWNOSALES

    const [items, setItems] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");

    const [slowPayload, setSlowPayload] = useState(null);

    const [months, setMonths] = useState(6);
    const slowPercent = 20;

    const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

    const proceed = async () => {
        setLoading(true);
        try {
            if (mode === "RESTOCK") {
                const data = await fetchItemSalesRateAndRestock();
                setItems(Array.isArray(data) ? data : []);
                setSlowPayload(null);
            } else {
                const data = await fetchItemSlowAndNoSales({months, slowPercent});
                setSlowPayload(data || null);
                setItems([]);
            }
            setLoaded(true);
        } catch (e) {
            alert(e?.message || "Failed to load analytics data.");
            setLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    const canPrint = loaded && !loading && (
        (mode === "RESTOCK" && items.length > 0) ||
        (mode === "SLOWNOSALES" && (slowPayload?.slowItems?.length > 0 || slowPayload?.noSalesItems?.length > 0))
    );

    const print = async () => {
        if (mode === "RESTOCK") {
            await ItemAnalyticsPrint(items, {leadTimeDays});
        } else {
            await ItemSlowNoSalesPrint(slowPayload, {});
        }
    };

    const sortedItems = useMemo(() => {
        const list = Array.isArray(items) ? [...items] : [];
        list.sort((a, b) => {
            const ad = a?.restock?.reorderDate ? dayjs(a.restock.reorderDate).valueOf() : Number.POSITIVE_INFINITY;
            const bd = b?.restock?.reorderDate ? dayjs(b.restock.reorderDate).valueOf() : Number.POSITIVE_INFINITY;
            if (ad !== bd) return ad - bd;
            return String(a?.itemName || "").localeCompare(String(b?.itemName || ""));
        });

        if (!globalFilter.trim()) return list;
        const q = globalFilter.trim().toLowerCase();
        return list.filter((it) => String(it?.itemName || "").toLowerCase().includes(q));
    }, [items, globalFilter]);

    const urgencyTag = (row) => {
        const reorderDate = row?.restock?.reorderDate;
        if (!reorderDate) return <Tag value="N/A" severity="secondary"/>;

        const days = dayjs(reorderDate).diff(dayjs().startOf("day"), "day");
        if (days < 0) return <Tag value="OVERDUE" severity="danger"/>;
        if (days <= 14) return <Tag value="URGENT" severity="warning"/>;
        return <Tag value="OK" severity="success"/>;
    };

    const rowExpansionTemplate = (row) => {
        const months = Array.isArray(row?.sales?.months) ? row.sales.months : [];
        return (
            <div style={{padding: "10px 12px"}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8}}>
                    <div style={{fontWeight: 700, color: "#111827"}}>Monthly Sold Details</div>
                    <div style={{fontSize: 12, color: "#6b7280"}}>
                        Valid months only (months with non-positive stock excluded)
                    </div>
                </div>

                {months.length === 0 ? (
                    <div style={{fontSize: 13, color: "#6b7280"}}>No valid months available for this item.</div>
                ) : (
                    <DataTable value={months} size="small" stripedRows>
                        <Column field="month" header="Month" style={{width: "160px"}}/>
                        <Column
                            field="soldQty"
                            header="Sold Qty"
                            body={(m) => (safeNum(m?.soldQty) ?? 0).toLocaleString()}
                            style={{width: "140px", textAlign: "right"}}
                        />
                    </DataTable>
                )}
            </div>
        );
    };

    const slowNoSales = slowPayload || {};
    const noSalesItems = Array.isArray(slowNoSales.noSalesItems) ? slowNoSales.noSalesItems : [];
    const slowItems = Array.isArray(slowNoSales.slowItems) ? slowNoSales.slowItems : [];

    return (
        <div style={{padding: 18}}>
            <MunshiMenu handle={handle}/>
            <Card
                title="Item Analytics"
                subTitle="Choose a report type, load data, and print a professional report."
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        gap: 12,
                        alignItems: "end",
                        marginBottom: 14,
                    }}
                >
                    <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                        <label style={{fontSize: 12, color: "#6b7280"}}>Report</label>
                        <select
                            value={mode}
                            onChange={(e) => {
                                setMode(e.target.value);
                                setLoaded(false);
                                setItems([]);
                                setSlowPayload(null);
                            }}
                            style={{padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db"}}
                        >
                            <option value="RESTOCK">Sales & Restock</option>
                            <option value="SLOWNOSALES">Slow / No-Sales Items</option>
                        </select>
                    </div>

                    <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
                        {mode === "SLOWNOSALES" && (
                            <>
                                <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                    <label style={{fontSize: 12, color: "#6b7280"}}>Months window</label>
                                    <input
                                        type="number"
                                        value={months}
                                        min={1}
                                        onChange={(e) => setMonths(Number(e.target.value || 6))}
                                        style={{padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", width: 140}}
                                    />
                                </div>
                                <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                    <label style={{fontSize: 12, color: "#6b7280"}}>Slow rule</label>
                                    <div style={{padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#f9fafb", width: 220}}>
                                        Bottom {slowPercent}% of sales
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={{display: "flex", gap: 10, alignItems: "end"}}>
                            <Button
                                label={loading ? "Loading..." : "Proceed"}
                                icon="pi pi-play"
                                onClick={proceed}
                                disabled={loading}
                                severity="success"
                            />
                            <Button
                                label="Print"
                                icon="pi pi-print"
                                onClick={print}
                                disabled={!canPrint}
                                severity="warning"
                                outlined
                            />
                        </div>
                    </div>
                </div>

                {loading && (
                    <div style={{display: "flex", justifyContent: "center", padding: 20}}>
                        <ProgressSpinner style={{width: "44px", height: "44px"}}/>
                    </div>
                )}

                {!loading && loaded && mode === "RESTOCK" && (
                    <div style={{marginTop: 6}}>
                        <DataTable
                            value={sortedItems}
                            dataKey="itemId"
                            stripedRows
                            paginator
                            rows={15}
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            responsiveLayout="scroll"
                            emptyMessage="No analytics data found."
                        >
                            <Column expander style={{width: "3rem"}}/>
                            <Column field="itemName" header="Item" sortable/>
                            <Column
                                header="Stock"
                                body={(r) => (safeNum(r?.currentStock) ?? 0).toLocaleString()}
                                style={{textAlign: "right", width: "120px"}}
                                sortable
                            />
                            <Column
                                header="Avg / Day"
                                body={(r) => {
                                    const v = safeNum(r?.sales?.avgDailySold);
                                    return v === null ? "-" : v.toFixed(4);
                                }}
                                style={{textAlign: "right", width: "120px"}}
                            />
                            <Column
                                header="Avg / Month"
                                body={(r) => {
                                    const v = safeNum(r?.sales?.avgMonthlySold);
                                    return v === null ? "-" : v.toFixed(2);
                                }}
                                style={{textAlign: "right", width: "120px"}}
                            />
                            <Column
                                header="Stockout"
                                body={(r) => r?.restock?.estimatedStockoutDate || "-"}
                                style={{textAlign: "center", width: "130px"}}
                            />
                            <Column
                                header="Reorder"
                                body={(r) => r?.restock?.reorderDate || "-"}
                                style={{textAlign: "center", width: "130px"}}
                                sortable
                            />
                            <Column
                                header="Urgency"
                                body={urgencyTag}
                                style={{textAlign: "center", width: "110px"}}
                            />
                            <Column
                                header="Suggested Qty"
                                body={(r) => (safeNum(r?.restock?.suggestedReorderQty) ?? 0).toLocaleString()}
                                style={{textAlign: "right", width: "140px", fontWeight: 700}}
                            />
                        </DataTable>
                    </div>
                )}

                {!loading && loaded && mode === "SLOWNOSALES" && (
                    <div style={{display: "grid", gridTemplateColumns: "1fr", gap: 16}}>
                        <Card title={`No Sales in Last ${slowNoSales?.params?.months ?? months} Months`} subTitle="Items that have not sold within the selected window.">
                            <DataTable value={noSalesItems} stripedRows paginator rows={15} responsiveLayout="scroll"
                                       emptyMessage="No items found.">
                                <Column field="itemName" header="Item" sortable/>
                                <Column field="currentStock" header="Stock" body={(r) => (r.currentStock ?? 0).toLocaleString()} style={{textAlign: "right", width: 120}}/>
                                <Column field="soldLastMonths" header="Sold (window)" body={(r) => (r.soldLastMonths ?? 0).toLocaleString()} style={{textAlign: "right", width: 140}}/>
                                <Column field="avgMonthlySold" header="Avg / Month" body={(r) => Number(r.avgMonthlySold ?? 0).toFixed(2)} style={{textAlign: "right", width: 140}}/>
                                <Column field="lastSaleDate" header="Last Sale" body={(r) => r.lastSaleDate || "-"} style={{textAlign: "center", width: 140}}/>
                                <Column field="daysSinceLastSale" header="Days Since" body={(r) => (r.daysSinceLastSale ?? "-")} style={{textAlign: "right", width: 120}}/>
                            </DataTable>
                        </Card>

                        <Card
                            title={`Slow-Moving Items (Bottom ${slowNoSales?.params?.slowPercent ?? slowPercent}%)`}
                            subTitle="Computed from items that DID sell in the selected window."
                        >
                            <DataTable value={slowItems} stripedRows paginator rows={15} responsiveLayout="scroll"
                                       emptyMessage="No items found.">
                                <Column field="itemName" header="Item" sortable/>
                                <Column field="currentStock" header="Stock" body={(r) => (r.currentStock ?? 0).toLocaleString()} style={{textAlign: "right", width: 120}}/>
                                <Column field="soldLastMonths" header="Sold (window)" body={(r) => (r.soldLastMonths ?? 0).toLocaleString()} style={{textAlign: "right", width: 140}}/>
                                <Column field="avgMonthlySold" header="Avg / Month" body={(r) => Number(r.avgMonthlySold ?? 0).toFixed(2)} style={{textAlign: "right", width: 140}}/>
                                <Column field="lastSaleDate" header="Last Sale" body={(r) => r.lastSaleDate || "-"} style={{textAlign: "center", width: 140}}/>
                                <Column field="daysSinceLastSale" header="Days Since" body={(r) => (r.daysSinceLastSale ?? "-")} style={{textAlign: "right", width: 120}}/>
                            </DataTable>
                        </Card>
                    </div>
                )}

                {!loading && !loaded && (
                    <div style={{padding: "10px 2px", color: "#6b7280", fontSize: 13}}>
                        Choose a report and click <b>Proceed</b>.
                    </div>
                )}
            </Card>
        </div>
    );
}